import { useCallback, useRef, useSyncExternalStore } from 'react'

import { LocalStorageItem, subscribeToLocalStorageKey } from '@/helpers/localStorageItem'

export const useLocalStorageItems = <Value>(items: LocalStorageItem<Value>[]): Value[] => {
    const snapshotRef = useRef<Value[]>([])

    const serializedKeys = JSON.stringify(items.map(item => item.key))

    const subscribe = useCallback((listener: () => void): (() => void) => {
        const keys: string[] = JSON.parse(serializedKeys)
        const unsubscribes = keys.map(key => subscribeToLocalStorageKey(key, listener))

        return () => unsubscribes.forEach(unsubscribe => unsubscribe())
    }, [serializedKeys])

    const getSnapshot = (): Value[] => {
        const values = items.map(item => item.get())
        const previous = snapshotRef.current

        if (values.length === previous.length && values.every((value, index) => Object.is(value, previous[index]))) {
            return previous
        }

        snapshotRef.current = values

        return values
    }

    return useSyncExternalStore(subscribe, getSnapshot)
}

export const useLocalStorageItem = <Value>(item: LocalStorageItem<Value>): [Value, (value: Value) => void] => {
    const [value] = useLocalStorageItems([item])

    return [value, item.set]
}
